import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from 'use-analytics';
import { z } from 'zod';
import i18n from '../../../Config/app/i18n';
import { trackAndNavigate } from '../../../Config/libs/analytics';
import { PostHogEventEnum, PostHogFeatureFlagEnum } from '../../enum/PostHogEvent.enum';
import errorMapper, { Error, ErrorCodes } from '../../utils/errorMapper';

const LoginValidator = z.object({
  username: z.string().email(),
  password: z.string().min(1, i18n.t('validation:password_required')),
  recaptcha_response_v3: z.string(),
  recaptcha_response_v2: z.string().optional(),
});

export type LoginForm = z.infer<typeof LoginValidator>;

export const useLogin = () => {
  const { t } = useTranslation('validation');
  const { track } = useAnalytics();
  const postHog = usePostHog();

  const [isPending, setIsPending] = useState(false);
  const [useV2, setUseV2] = useState(false);

  const [tooManyRequests, setTooManyRequests] = useState<boolean>(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setError,
    setValue,
    register,
  } = useForm<LoginForm>({
    resolver: zodResolver(LoginValidator),
  });

  register('recaptcha_response_v3', { required: true });

  const mutation = useMutation<{ user_id: string }, Error<LoginForm>, LoginForm>({
    mutationKey: ['accounts/login'],
    throwOnError: false,
    onSuccess: (response) => {
      // Identify user in PostHog so this event and future events are linked to the user
      postHog.identify(response.user_id);

      // Get the A/B test variant for tracking
      const backgroundVariant = postHog.getFeatureFlag(PostHogFeatureFlagEnum.SignupBackgroundTest);

      // Capture in PostHog for experiment analysis (before reload)
      postHog.capture(PostHogEventEnum.LoginCompleted, {
        background_variant: backgroundVariant,
      });

      trackAndNavigate(
        'login_form_success',
        {
          event_category: 'User login',
          event_label: 'login_form',
          background_variant: backgroundVariant ?? 'unknown',
        },
        () => window.location.reload(),
      );
    },
    onError: (error) => {
      setIsPending(false);
      track('login_form_error', { event_category: 'User login', event_label: 'login_form' });
      // eslint-disable-next-line
      errorMapper(error.response?.data?.errors || {}, (field, message, code) => {
        if (field === 'recaptcha_response_v3') {
          setUseV2(true);
          return;
        }

        if (code && code === ErrorCodes.TOO_MANY_REQUESTS) {
          setTooManyRequests(true);
          return;
        }

        setError('username', { message: t('invalid_credentials') });
        setError('password', { message: t('invalid_credentials') });
      });
    },
  });

  const onSubmit = (data: LoginForm) => {
    track('login_form_submit', { event_category: 'User login', event_label: 'login_form' });
    setIsPending(true);
    setTooManyRequests(false);
    return mutation.mutate(data);
  };

  const onVerify = useCallback(
    (token: string) => {
      setValue('recaptcha_response_v3', token);
    },
    [mutation.submittedAt],
  );

  return {
    control,
    useV2,
    register,
    errors,
    handleSubmit,
    onSubmit,
    onVerify,
    isPending,
    tooManyRequests,
  };
};
